import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BulkEditBody,
  BulkEditResult,
  BulkItems,
  MbApplyBody,
  MbApplyResult,
  MbProposal,
  MbReleaseCandidate,
  LibraryReport,
  SmartPreview,
  SmartRules,
  DeletePreview,
  DeleteResult,
  DeleteScope,
  DeleteStatus,
  AdminStats,
  Album,
  AlbumDetail,
  AlbumSort,
  AppConfig,
  Artist,
  ArtistDetail,
  Composer,
  ComposerDetail,
  Favorites,
  FavoriteKind,
  FolderListing,
  Genre,
  HomeData,
  Lyrics,
  Me,
  Page,
  PersonInfo,
  Playlist,
  PlaylistDetail,
  AdminUser,
  ScanStatus,
  SearchResults,
  Series,
  SeriesDetail,
  Track,
  TrackSort,
  EditHistoryItem,
  EditKind,
  EditSaveBody,
  EditSaveResult,
  EditTarget,
  CoverCandidate,
  LyricsCandidate,
  UploadResult,
} from '@pujolisique/shared';
import { API_BASE, api } from './client';

const enc = encodeURIComponent;

export const useConfig = () => useQuery({ queryKey: ['config'], queryFn: () => api<AppConfig>('/api/config'), staleTime: Infinity });
export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/api/me'), staleTime: 5 * 60_000, retry: false });
export const useHome = () => useQuery({ queryKey: ['home'], queryFn: () => api<HomeData>('/api/home'), staleTime: 60_000 });
export const useHistory = () => useQuery({ queryKey: ['history'], queryFn: () => api<(Track & { playedAt: number })[]>('/api/history?limit=100') });

function usePaged<T>(key: unknown[], path: string, pageSize = 60) {
  return useInfiniteQuery({
    queryKey: key,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<Page<T>>(`${path}${path.includes('?') ? '&' : '?'}offset=${pageParam}&limit=${pageSize}`),
    getNextPageParam: (last) => (last.hasMore ? last.offset + last.items.length : undefined),
  });
}

export const useAlbums = (sort: AlbumSort) => usePaged<Album>(['albums', sort], `/api/albums?sort=${sort}`);
export const useArtists = () => usePaged<Artist>(['artists'], '/api/artists', 100);
export const useComposers = () => usePaged<Composer>(['composers'], '/api/composers', 100);
export const useTrackPages = (sort: TrackSort) => usePaged<Track>(['tracks', sort], `/api/tracks?sort=${sort}`, 100);

export const useAlbum = (id: string) => useQuery({ queryKey: ['album', id], queryFn: () => api<AlbumDetail>(`/api/albums/${enc(id)}`) });
export const useArtist = (id: string) => useQuery({ queryKey: ['artist', id], queryFn: () => api<ArtistDetail>(`/api/artists/${enc(id)}`) });
export const useComposer = (id: string) => useQuery({ queryKey: ['composer', id], queryFn: () => api<ComposerDetail>(`/api/composers/${enc(id)}`) });
export const usePersonInfo = (kind: 'artist' | 'composer', id: string, name?: string) =>
  useQuery({
    queryKey: ['info', kind, id],
    queryFn: () => api<{ info: PersonInfo | null }>(`/api/info/${kind}/${enc(id)}${name ? `?name=${enc(name)}` : ''}`),
    staleTime: Infinity,
    enabled: !!name,
  });
export const useGenres = () => useQuery({ queryKey: ['genres'], queryFn: () => api<Genre[]>('/api/genres'), staleTime: 10 * 60_000 });
export const useGenre = (id: string) =>
  useQuery({ queryKey: ['genre', id], queryFn: () => api<{ id: string; albums: Album[]; tracks: Track[] }>(`/api/genres/${enc(id)}`) });
export const useFolder = (id?: string) =>
  useQuery({ queryKey: ['folder', id ?? ''], queryFn: () => api<FolderListing>(id ? `/api/folders/${enc(id)}` : '/api/folders') });
/** Suggestions pendant la frappe (quelques résultats de chaque sorte) */
export const useSuggest = (q: string) =>
  useQuery({
    queryKey: ['suggest', q],
    queryFn: ({ signal }) => api<SearchResults>(`/api/search?q=${enc(q)}&limit=5`, { signal }),
    enabled: q.trim().length > 0,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
export const useSearch = (q: string) =>
  useQuery({
    queryKey: ['search', q],
    queryFn: ({ signal }) => api<SearchResults>(`/api/search?q=${enc(q)}&limit=24`, { signal }),
    enabled: q.trim().length > 0,
    placeholderData: (prev) => prev,
  });
export const useLyrics = (id?: string) =>
  useQuery({ queryKey: ['lyrics', id], queryFn: () => api<{ lyrics: Lyrics | null }>(`/api/lyrics/${enc(id!)}`), enabled: !!id, staleTime: Infinity });
export const useFavorites = () => useQuery({ queryKey: ['favorites'], queryFn: () => api<Favorites>('/api/favorites'), staleTime: 30_000 });
export const usePlaylists = () => useQuery({ queryKey: ['playlists'], queryFn: () => api<Playlist[]>('/api/playlists'), staleTime: 30_000 });
export const usePlaylist = (id: string) => useQuery({ queryKey: ['playlist', id], queryFn: () => api<PlaylistDetail>(`/api/playlists/${enc(id)}`) });
export const useAdminStats = (enabled: boolean) => useQuery({ queryKey: ['admin-stats'], queryFn: () => api<AdminStats>('/api/admin/stats'), enabled });

export const useStories = () => useQuery({ queryKey: ['stories'], queryFn: () => api<Series[]>('/api/stories'), staleTime: 30_000 });
export const useSeries = (id: string) => useQuery({ queryKey: ['series', id], queryFn: () => api<SeriesDetail>(`/api/stories/${enc(id)}`) });
export const fetchSeries = (id: string) => api<SeriesDetail>(`/api/stories/${enc(id)}`);
export const useUploads = (all = false) => useQuery({ queryKey: ['uploads', all], queryFn: () => api<Track[]>(`/api/uploads${all ? '?all=true' : ''}`) });
export const useAdminUsers = (enabled: boolean) => useQuery({ queryKey: ['admin-users'], queryFn: () => api<AdminUser[]>('/api/admin/users'), enabled });
export const useScanStatus = (enabled: boolean) =>
  useQuery({ queryKey: ['scan'], queryFn: () => api<ScanStatus>('/api/admin/scan'), enabled, refetchInterval: (q) => (q.state.data?.running ? 1500 : 15_000) });

export const saveProgress = (trackId: string, position: number, finished?: boolean) =>
  api<{ position: number; finished: boolean }>(`/api/progress/${enc(trackId)}`, { method: 'PUT', json: { position, finished } });
export const resetProgress = (trackIds: string[]) => api('/api/progress', { method: 'DELETE', json: { trackIds } });

/** Envoi avec suivi de progression (XHR, le fetch ne fournit pas l'avancement de l'envoi) */
export function uploadFiles(libraryId: string, folder: string, files: File[], onProgress: (ratio: number) => void): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('libraryId', libraryId);
    form.append('folder', folder);
    for (const f of files) form.append('files', f, f.name);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/api/uploads`);
    xhr.withCredentials = !API_BASE;
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      try {
        const body = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) resolve(body);
        else reject(new Error(body.message ?? `Erreur ${xhr.status}`));
      } catch {
        reject(new Error(`Erreur ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Envoi interrompu (connexion perdue)'));
    xhr.send(form);
  });
}

export const fetchMix = (kind: string, id: string) => api<Track[]>(`/api/mix/${kind}/${enc(id)}?limit=60`);
export const fetchAlbum = (id: string) => api<AlbumDetail>(`/api/albums/${enc(id)}`);
export const fetchPlaylist = (id: string) => api<PlaylistDetail>(`/api/playlists/${enc(id)}`);
export const fetchRandomTracks = (genre?: string) => api<Page<Track>>(`/api/tracks?sort=random&limit=60${genre ? `&genre=${enc(genre)}` : ''}`);

/* ------------------------------ Mutations ------------------------------ */

export function useFavoriteToggle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, id, value }: { kind: FavoriteKind; id: string; value: boolean }) =>
      api(`/api/favorites/${kind}/${enc(id)}`, { method: value ? 'PUT' : 'DELETE' }),
    onSuccess: () => {
      // L'état « favori » est porté par les pistes de toutes les vues : on les rafraîchit.
      const skip = new Set(['lyrics', 'config', 'me', 'info', 'admin-stats']);
      qc.invalidateQueries({ predicate: (q) => !skip.has(String(q.queryKey[0])) });
    },
  });
}

export function usePlaylistMutations() {
  const qc = useQueryClient();
  const done = (id?: string) => {
    qc.invalidateQueries({ queryKey: ['playlists'] });
    if (id) qc.invalidateQueries({ queryKey: ['playlist', id] });
  };
  return {
    create: useMutation({
      mutationFn: (v: { name: string; trackIds: string[] }) => api<Playlist>('/api/playlists', { method: 'POST', json: v }),
      onSuccess: () => done(),
    }),
    add: useMutation({
      mutationFn: (v: { id: string; trackIds: string[] }) => api(`/api/playlists/${enc(v.id)}/tracks`, { method: 'POST', json: { trackIds: v.trackIds } }),
      onSuccess: (_d, v) => done(v.id),
    }),
    remove: useMutation({
      mutationFn: (v: { id: string; entryIds: string[] }) => api<{ ok: boolean; id: string }>(`/api/playlists/${enc(v.id)}/tracks`, { method: 'DELETE', json: { entryIds: v.entryIds } }),
      onSuccess: (_d, v) => done(v.id),
    }),
    rename: useMutation({
      mutationFn: (v: { id: string; name: string }) => api(`/api/playlists/${enc(v.id)}`, { method: 'PATCH', json: { name: v.name } }),
      onSuccess: (_d, v) => done(v.id),
    }),
    del: useMutation({
      mutationFn: (id: string) => api(`/api/playlists/${enc(id)}`, { method: 'DELETE' }),
      onSuccess: () => done(),
    }),
  };
}

/* ------------------------------ Édition ------------------------------ */

export const useEditTarget = (kind: EditKind, id: string) =>
  useQuery({ queryKey: ['edit', kind, id], queryFn: () => api<EditTarget>(`/api/edit/${kind}/${enc(id)}`), staleTime: 0 });
export const useEditHistory = () => useQuery({ queryKey: ['edits'], queryFn: () => api<EditHistoryItem[]>('/api/edits') });

export const saveEdit = (kind: EditKind, id: string, body: EditSaveBody) =>
  api<EditSaveResult>(`/api/edit/${kind}/${enc(id)}`, { method: 'PUT', json: body });
export const revertEdit = (kind: EditKind, id: string) => api<EditSaveResult>(`/api/edit/${kind}/${enc(id)}`, { method: 'DELETE' });
export function uploadCover(kind: EditKind, id: string, src: File | string) {
  if (typeof src === 'string') return api<{ ok: true }>(`/api/edit/${kind}/${enc(id)}/cover`, { method: 'POST', json: { url: src } });
  const fd = new FormData();
  fd.append('file', src);
  return api<{ ok: true }>(`/api/edit/${kind}/${enc(id)}/cover`, { method: 'POST', body: fd });
}
export const removeCover = (kind: EditKind, id: string) => api<{ ok: true }>(`/api/edit/${kind}/${enc(id)}/cover`, { method: 'DELETE' });
export const searchCovers = (kind: EditKind, id: string, q?: string) =>
  api<CoverCandidate[]>(`/api/edit/${kind}/${enc(id)}/cover-search${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const searchLyrics = (id: string, q?: string) =>
  api<LyricsCandidate[]>(`/api/edit/track/${enc(id)}/lyrics-search${q ? `?q=${encodeURIComponent(q)}` : ''}`);

/* Correction groupée */
export const bulkEdit = (body: BulkEditBody) => api<BulkEditResult>('/api/edit/bulk', { method: 'POST', json: body });
export const bulkItems = (albums: string[], tracks: string[]) => api<BulkItems>('/api/edit/bulk/items', { method: 'POST', json: { albums, tracks } });

/* Remplissage depuis MusicBrainz */
export const mbCandidates = (albumId: string, q?: string) =>
  api<MbReleaseCandidate[]>(`/api/edit/album/${enc(albumId)}/musicbrainz${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const mbProposal = (albumId: string, releaseId: string) => api<MbProposal>(`/api/edit/album/${enc(albumId)}/musicbrainz/${enc(releaseId)}`);
export const mbApply = (albumId: string, releaseId: string, body: MbApplyBody) =>
  api<MbApplyResult>(`/api/edit/album/${enc(albumId)}/musicbrainz/${enc(releaseId)}`, { method: 'POST', json: body });

/* ---------------------- Suppression définitive (admin) ---------------------- */
export const useDeleteStatus = (enabled: boolean) =>
  useQuery({ queryKey: ['delete-status'], queryFn: () => api<DeleteStatus>('/api/admin/delete/status'), enabled });
export const previewDelete = (scope: DeleteScope) => api<DeletePreview>('/api/admin/delete/preview', { method: 'POST', json: { scope } });
export const deleteFiles = (scope: DeleteScope, token: string) => api<DeleteResult>('/api/admin/delete', { method: 'POST', json: { scope, token, confirm: true } });

/* ------------------------- Playlists intelligentes ------------------------- */
export const previewSmart = (rules: SmartRules) => api<SmartPreview>('/api/playlists/preview', { method: 'POST', json: { rules } });
export const createSmartPlaylist = (v: { name: string; rules: SmartRules; public?: boolean }) => api<Playlist>('/api/playlists', { method: 'POST', json: v });
export const updatePlaylist = (id: string, v: { name?: string; rules?: SmartRules; public?: boolean }) =>
  api<{ ok: true }>(`/api/playlists/${enc(id)}`, { method: 'PATCH', json: v });

/* ------------------------- Rapport (administrateurs) ------------------------- */
export const useLibraryReport = (enabled = true) =>
  useQuery({ queryKey: ['library-report'], queryFn: () => api<LibraryReport>('/api/admin/report'), enabled, staleTime: 60_000 });
